<template>
  <div
      :class="isModal ? 'w-full' : 'mx-auto max-w-md rounded-2xl bg-white p-8 shadow-xl dark:bg-gray-800'"
  >
    <component
        :is="isModal ? 'h2' : 'h1'"
        :class="isModal
          ? 'mb-4 text-start text-lg font-bold tracking-tight text-gray-900 dark:text-gray-50'
          : 'mb-6 text-center text-3xl font-extrabold text-gray-900 dark:text-gray-100'"
    >{{ $t('auth.forgot_password') }}</component>

    <form :class="isModal ? 'space-y-4' : 'space-y-6'" @submit.prevent="onSubmit">
      <div>
        <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-300">{{ $t('auth.email') }}</label>
        <input
            id="email"
            v-model="form.email"
            type="email"
            placeholder="you@example.com"
            required
            :class="inputClass"
        />
      </div>

      <div v-if="status" class="rounded-lg bg-emerald-50 px-3 py-2 text-center text-sm text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
        {{ status }}
      </div>
      <div v-if="error" class="rounded-lg bg-red-50 px-3 py-2 text-center text-sm text-red-600 dark:bg-red-950/40 dark:text-red-400">
        {{ error }}
      </div>

      <button
          type="submit"
          :aria-busy="loading"
          :class="[
            'flex w-full cursor-pointer items-center justify-center gap-2 font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50',
            isModal
              ? 'rounded-xl bg-linear-to-r from-indigo-600 to-violet-600 py-3.5 text-[15px] shadow-lg shadow-indigo-500/25 hover:from-indigo-500 hover:to-violet-500'
              : 'rounded-lg bg-linear-to-r from-yellow-500 to-yellow-600 py-3 hover:from-yellow-600 hover:to-yellow-700',
          ]"
          :disabled="loading"
      >
        <Loader v-if="loading" class="h-5 w-5 shrink-0 animate-spin"/>
        <span>{{ loading ? $t('loading') + '...' : $t('auth.send_reset_link') }}</span>
      </button>

      <p class="mt-5 text-center text-sm text-gray-600 dark:text-gray-400">
        <router-link 
            v-if="!isModal" 
            to="/login" 
            class="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400">
          {{ $t('auth.back_to_login') }}
        </router-link>
        <a 
            v-else
            class="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400" 
            @click="$emit('switch-tab', 'login')">
          {{ $t('auth.back_to_login') }}
        </a>
      </p>
    </form>
  </div>
</template>

<script setup>
import { computed, reactive, ref, defineProps, defineEmits } from 'vue'
import axios from 'axios'
import { Loader } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { getApiErrorMessage } from '@/utils/error-handler'

const props = defineProps({
  isModal: { type: Boolean, default: false }
})

defineEmits(['switch-tab', 'close-modal'])

const { t } = useI18n()
const form = reactive({ email: '' })
const error = ref('')
const status = ref('')
const loading = ref(false)

const inputClass = computed(() => [
  'mt-1 block w-full border px-3.5 py-3 text-gray-900 placeholder-gray-400 transition focus:outline-none focus:ring-2 dark:text-gray-100',
  props.isModal
    ? 'rounded-xl border-gray-200 bg-white/90 focus:border-indigo-500 focus:ring-indigo-500/30 dark:border-gray-600 dark:bg-gray-800/60 dark:focus:border-indigo-400 dark:focus:ring-indigo-400/25'
    : 'rounded-lg border-gray-300 bg-gray-50 focus:ring-yellow-500 dark:border-gray-600 dark:bg-gray-700',
])

async function onSubmit () {
  error.value = ''
  status.value = ''
  loading.value = true
  try {
    const { data } = await axios.post('/api/forgot-password', { email: form.email })
    status.value = data.status || t('auth.reset_link_sent')
  } catch (e) {
    error.value = getApiErrorMessage(e, t, 'auth.unknown_error')
  } finally {
    loading.value = false
  }
}
</script>
